import { PurchasePointsEarningInvitation } from "@/features/purchasePoints/components/Balance/PurchasePointsEarningInvitation";
import { PurchasePointsValidityNotice } from "@/features/purchasePoints/components/Balance/PurchasePointsValidityNotice";
import { BALANCE_FIGURES_ROW, BALANCE_FIGURES_ROW_CENTERED, BalanceFigure } from "@/features/purchasePoints/components/BalanceFigure";
import { PurchasePointsConvertCta } from "@/features/purchasePoints/components/PurchasePointsConvertCta";
import { PurchasePointsErrorState } from "@/features/purchasePoints/components/PurchasePointsErrorState";
import { LatestRewards } from "@/features/purchasePoints/components/SummaryWidget/LatestRewards";
import {
  PURCHASE_POINTS_AVAILABLE_LABEL,
  PURCHASE_POINTS_BALANCE_ERROR_MESSAGE,
  PURCHASE_POINTS_NONE_LABEL,
  PURCHASE_POINTS_PENDING_EXPLAINER,
  PURCHASE_POINTS_PENDING_LABEL,
  PURCHASE_POINTS_TITLE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import type { PurchasePointsError } from "@/features/purchasePoints/errors/purchasePointsError";
import type { PurchasePointsBalance } from "@/features/purchasePoints/types/purchasePointsBalance.type";
import type { PurchasePointsReward } from "@/features/purchasePoints/types/purchasePointsReward.type";
import type { SummaryState } from "@/features/purchasePoints/utils/readSummaryState";

type SummaryBodyProps = {
  state: SummaryState
  balance: PurchasePointsBalance | null
  balanceError: PurchasePointsError | null
  onRetryBalance: () => void
  rewards: PurchasePointsReward[]
  rewardsError: PurchasePointsError | null
  onRetryRewards: () => void
};

const SummarySkeleton = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label={PURCHASE_POINTS_TITLE}
    data-testid="purchase-points-summary-skeleton"
    className="h-40 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700"
  />
);

SummarySkeleton.displayName = "SummarySkeleton";

const FailedBody = ({ error, onRetry }: { error: PurchasePointsError, onRetry: () => void }) => (
  <div role="alert" data-testid="purchase-points-summary-error">
    <PurchasePointsErrorState error={error} onRetry={onRetry} fallbackMessage={PURCHASE_POINTS_BALANCE_ERROR_MESSAGE} />
  </div>
);

FailedBody.displayName = "FailedBody";

const DormantBody = () => (
  <div data-testid="purchase-points-summary-dormant">
    <p
      data-testid="purchase-points-none"
      className="text-xl font-semibold leading-none text-gray-500 dark:text-gray-400"
    >
      {PURCHASE_POINTS_NONE_LABEL}
    </p>
    <PurchasePointsEarningInvitation />
  </div>
);

DormantBody.displayName = "DormantBody";

type ReadyBodyProps = {
  balance: PurchasePointsBalance
  rewards: PurchasePointsReward[]
  rewardsError: PurchasePointsError | null
  onRetryRewards: () => void
};

const ReadyBody = ({
  balance, rewards, rewardsError, onRetryRewards,
}: ReadyBodyProps) => (
  <>
    <PurchasePointsValidityNotice />

    <div className={BALANCE_FIGURES_ROW}>
      <div className={BALANCE_FIGURES_ROW_CENTERED}>
        <BalanceFigure
          label={PURCHASE_POINTS_AVAILABLE_LABEL}
          points={balance.available_points}
          testId="purchase-points-available"
          isPrimary
        />
        <PurchasePointsConvertCta availablePoints={balance.available_points} pointsPerEuro={balance.points_per_euro} />
      </div>

      <BalanceFigure
        label={PURCHASE_POINTS_PENDING_LABEL}
        points={balance.pending_points}
        testId="purchase-points-pending"
        explainer={PURCHASE_POINTS_PENDING_EXPLAINER}
      />
    </div>

    {/* <PurchasePointsConvertCta availablePoints={balance.available_points} pointsPerEuro={balance.points_per_euro} /> */}

    <hr aria-hidden="true" className="my-5 border-gray-200 dark:border-gray-700" />

    <LatestRewards rewards={rewards} error={rewardsError} retry={onRetryRewards} />
  </>
);

ReadyBody.displayName = "ReadyBody";

export const SummaryBody = ({
  state, balance, balanceError, onRetryBalance, rewards, rewardsError, onRetryRewards,
}: SummaryBodyProps) => {
  if (state === "loading") return <SummarySkeleton />;
  if (state === "failed" && balanceError) return <FailedBody error={balanceError} onRetry={onRetryBalance} />;
  if (state === "dormant") return <DormantBody />;

  if (state === "ready" && balance) {
    return (
      <ReadyBody balance={balance} rewards={rewards} rewardsError={rewardsError} onRetryRewards={onRetryRewards} />
    );
  }

  return null;
};

SummaryBody.displayName = "SummaryBody";
